import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dailyActivityService } from '../services/dailyActivityService';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import PDFThumbnail from '../components/PDFThumbnail';
import PDFPageDisplayForDailyActs from '../components/PDFPageDisplayForDailyActs';
import ConsistentLoader from '../components/ConsistentLoader';
import { FaExpand } from 'react-icons/fa';
import KidBackground from '../ui/backgrounds/KidBackground';
import RoomBackground from '../components/artHistoryRoom/RoomBackground';
import RoomSceneWrapper from '../components/artHistoryRoom/RoomSceneWrapper';
import BackButton from '../components/BackButton';
import { Home, LogOut, LayoutDashboard } from 'lucide-react';

const DailyArtActivity = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  const [userProgress, setUserProgress] = useState(null);
  const [activities, setActivities] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showExpandedView, setShowExpandedView] = useState(false);
  const [hasPDFPages, setHasPDFPages] = useState(false);
  const [showNextActivity, setShowNextActivity] = useState(false);
  const [showNoArtworkPopup, setShowNoArtworkPopup] = useState(false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0); // Force re-render counter

  // Deep-link: activity number/id and optional submission fallback
  const requestedActivityId = searchParams.get('activityId');
  const requestedSubmissionId = searchParams.get('submissionId');
  const getDailySnapshotKey = (uid, activityId) =>
    `daily-art-activity-snapshot:${uid}:${activityId || 'next'}`;

  // Get current activity - moved here so it's available throughout the component
  const currentActivity = activities[currentCardIndex];

  useEffect(() => {
    if (!user?.uid) return;
    // Instant paint from previous state (premium app behavior): show cached content first.
    try {
      if (!requestedActivityId) {
        const raw = sessionStorage.getItem(getDailySnapshotKey(user.uid, requestedActivityId));
        if (raw) {
          const snap = JSON.parse(raw);
          if (Array.isArray(snap.activities)) setActivities(snap.activities);
          if (Array.isArray(snap.allActivities)) setAllActivities(snap.allActivities);
          if (typeof snap.currentCardIndex === 'number') setCurrentCardIndex(snap.currentCardIndex);
          if (snap.userProgress) setUserProgress(snap.userProgress);
          setHasLoadedOnce(true);
          setLoading(false);
        }
      }
    } catch (e) {
      console.warn('Could not hydrate DailyArtActivity snapshot:', e);
    }

    loadUserProgress();
  }, [user?.uid, requestedActivityId, requestedSubmissionId]);

  useEffect(() => {
    if (!user?.uid) return;
    if (!activities?.length && !allActivities?.length && !userProgress) return;
    try {
      const snapshot = {
        activities,
        allActivities,
        currentCardIndex,
        userProgress,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(
        getDailySnapshotKey(user.uid, requestedActivityId),
        JSON.stringify(snapshot)
      );
    } catch (e) {
      console.warn('Could not persist DailyArtActivity snapshot:', e);
    }
  }, [user?.uid, requestedActivityId, activities, allActivities, currentCardIndex, userProgress]);

  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  // Preload current activity page image so opening "Try now" feels instant.
  useEffect(() => {
    const src = currentActivity?.imageUrl;
    if (!src) return;
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    } catch (_) {}
  }, [currentActivity?.id, currentActivity?.imageUrl]);

  const resolveRequestedActivity = async (sortedAll) => {
    console.log('[DailyArtActivity] Deep link params:', {
      requestedActivityId,
      requestedSubmissionId,
      href: window.location.href,
    });
    if (requestedActivityId && /^\d+$/.test(String(requestedActivityId).trim())) {
      const fetched = await dailyActivityService.fetchActivityByNumber(requestedActivityId);
      if (fetched) {
        console.log('[DailyArtActivity] Resolved activity:', {
          requested: requestedActivityId,
          id: fetched.id,
          title: fetched.title,
          image: fetched.imageUrl?.slice(-24),
        });
        return fetched;
      }
    } else if (requestedActivityId) {
      const match = dailyActivityService.findDailyActivityFromList(sortedAll, requestedActivityId);
      if (match) return match;
    }
    if (requestedSubmissionId) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, 'submissions', requestedSubmissionId));
        if (snap.exists()) {
          const subActivityId = snap.data()?.activityId;
          if (subActivityId) {
            return dailyActivityService.findDailyActivityFromList(sortedAll, subActivityId);
          }
        }
      } catch (err) {
        console.warn('[DailyArtActivity] Could not resolve activity from submission:', err);
      }
    }
    return null;
  };

  const loadUserProgress = async () => {
    try {
      setLoading(true);
      
      // Step 1: Load activities first (fast, cached) - show immediately
      // Force refresh on mount to ensure we get latest activities
      const allActivitiesData = await dailyActivityService.getAllActivities(true);
      const sortedAll = [...allActivitiesData].sort((a, b) => {
        const ai = Number(a.id);
        const bi = Number(b.id);
        if (Number.isNaN(ai) || Number.isNaN(bi)) {
          return String(a.id).localeCompare(String(b.id));
        }
        return ai - bi;
      });
      setAllActivities(sortedAll);

      // Deep-link: open the exact requested activity immediately (no flash of Activity 1)
      const deepLinkTarget = requestedActivityId || requestedSubmissionId;
      if (deepLinkTarget) {
        const match = await resolveRequestedActivity(sortedAll);
        if (match) {
          setActivities([match]);
          setCurrentCardIndex(0);
          setShowExpandedView(false);
          console.log('🎯 Showing requested activity:', match.id, match.title);
        } else {
          console.warn('[DailyArtActivity] Requested activity not found:', {
            requestedActivityId,
            requestedSubmissionId,
          });
          setActivities([]);
        }
      } else if (sortedAll.length > 0) {
        setActivities([sortedAll[0]]);
        setCurrentCardIndex(0);
      }

      // Step 2: Load progress in parallel with timeouts (non-blocking)
      const [firestoreCompleted, backendProgress] = await Promise.allSettled([
        // Firestore query with 2s timeout
        Promise.race([
          (async () => {
            try {
              const completedQuery = query(
                collection(db, 'userActivities'),
                where('userId', '==', user.uid),
                where('type', '==', 'daily_activity'),
                where('isCompleted', '==', true)
              );
              const completedSnap = await getDocs(completedQuery);
              return completedSnap.docs.map(doc => {
                const data = doc.data();
                return Number(data.activityId) || data.activityId;
              });
            } catch (err) {
              console.warn('⚠️ Firestore query failed:', err);
              return [];
            }
          })(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore timeout')), 2000))
        ]).catch(() => []),
        // Backend progress with 2s timeout
        Promise.race([
          dailyActivityService.getUserProgress(user.uid),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Backend timeout')), 2000))
        ]).catch(() => null)
      ]);
      
      // Process completed IDs - merge from both sources
      let completedIds = [];
      
      if (firestoreCompleted.status === 'fulfilled' && firestoreCompleted.value) {
        completedIds = [...completedIds, ...firestoreCompleted.value];
        console.log('✅ Firestore completed IDs:', firestoreCompleted.value);
      }
      
      if (backendProgress.status === 'fulfilled' && backendProgress.value?.completedActivities) {
        const backendIds = backendProgress.value.completedActivities.map(c => Number(c.activityId));
        console.log('✅ Backend completed IDs:', backendIds);
        completedIds = Array.from(new Set([...completedIds, ...backendIds]));
      }
      
      const uniqueCompletedIds = completedIds.map(id => Number(id)).filter(id => !isNaN(id));
      console.log('✅ Final merged completed IDs:', uniqueCompletedIds);

      const progress = {
        completedActivities: uniqueCompletedIds.map(id => ({ activityId: id })),
        totalActivities: sortedAll.length
      };
      setUserProgress(progress);

      // Re-apply requested activity after progress load (keeps exact activity, not "next incomplete")
      if (requestedActivityId || requestedSubmissionId) {
        const requestedActivity = await resolveRequestedActivity(sortedAll);
        if (requestedActivity) {
          setActivities([requestedActivity]);
          setCurrentCardIndex(0);
        }
      } else {
      // Filter out completed activities - only show incomplete ones
      const incompleteActivities = sortedAll.filter(activity =>
        !uniqueCompletedIds.includes(Number(activity.id))
      );
      
      // Update to show the correct incomplete activity
      if (incompleteActivities.length > 0) {
        const nextActivity = incompleteActivities[0];
        console.log('🔍 DEBUG: Setting next activity:', {
          activityId: nextActivity.id,
          title: nextActivity.title,
          allActivitiesFirst5: sortedAll.slice(0, 5).map(a => ({ id: a.id, title: a.title })),
          incompleteFirst5: incompleteActivities.slice(0, 5).map(a => ({ id: a.id, title: a.title }))
        });
        setActivities([nextActivity]);
        setCurrentCardIndex(0);
        console.log('🔄 Loaded user progress:', {
          completedIds: uniqueCompletedIds,
          allActivities: sortedAll.length,
          totalIncomplete: incompleteActivities.length,
          currentActivity: nextActivity?.id
        });
      } else {
        setActivities([]);
        setCurrentCardIndex(0);
        console.log('🎉 All activities completed!');
        }
      }
      
    } catch (error) {
      console.error('Error loading activities:', error);
      setActivities([]);
      setUserProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const completeActivity = async (activityId) => {
    try {
      console.log('✅ Completing activity:', activityId);
      const updatedProgress = await dailyActivityService.completeActivity(user.uid, activityId);
      setUserProgress(updatedProgress);
      console.log('✅ Activity completed, updated progress:', updatedProgress);
      
      // Immediately refresh to show next activity
      await refreshUserProgress();
    } catch (error) {
      console.error('Error completing activity:', error);
      throw error; // Re-throw so caller can handle it
    }
  };

  const formatTimeUntilNext = (nextResetTime) => {
    const now = new Date();
    const nextReset = new Date(nextResetTime);
    const diff = nextReset - now;
    
    if (diff <= 0) return 'Available now!';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m until next activity`;
  };

  // After museum share decision: go to daily activities dashboard
  const handleShareFlowComplete = async (isShared) => {
    const completedActivityId =
      currentActivity?.id ?? activities[currentCardIndex]?.id ?? requestedActivityId;

    console.log('✅ Share flow finished, navigating to dashboard', {
      isShared,
      completedActivityId,
    });

    setShowExpandedView(false);
    setShowNextActivity(false);
    setShowCompletionPopup(false);

    try {
      await refreshUserProgress();
    } catch (error) {
      console.warn('⚠️ Could not refresh progress before dashboard navigation:', error);
    }

    const params = new URLSearchParams();
    if (completedActivityId) params.set('activityId', String(completedActivityId));
    if (isShared) params.set('shared', '1');
    const query = params.toString();
    navigate(query ? `/daily-activities?${query}` : '/daily-activities');
  };

  const handleNoArtworkAttempt = () => {
    setShowNoArtworkPopup(true);
  };

  const handleActivityCompletion = async () => {
    try {
      console.log('🎯 handleActivityCompletion called - UI update handler');
      console.log('🎯 Current state before update:', {
        showExpandedView,
        showCompletionPopup,
        activitiesCount: activities.length,
        currentCardIndex
      });
      
      // Close expanded view IMMEDIATELY - use functional update to ensure it happens
      console.log('✅ Closing expanded view immediately...');
      setShowExpandedView(prev => {
        console.log('✅ setShowExpandedView called, previous value:', prev);
        return false;
      });
      
      // Force a re-render
      setForceUpdate(prev => prev + 1);
      
      // Show popup FIRST, then reload data in background
      // This gives immediate feedback to the user
      console.log('✅ Showing completion popup immediately...');
      setShowCompletionPopup(prev => {
        console.log('✅ setShowCompletionPopup called, previous value:', prev);
        return true;
      });
      
      // Force another re-render
      setForceUpdate(prev => prev + 1);
      
      // Wait a moment for UI to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Now reload progress - wait longer for Firestore eventual consistency
      console.log('🔄 Waiting for Firestore to update (1.5 seconds)...');
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Reload progress to get next activity
      console.log('🔄 Reloading progress to get next activity...');
      
      // Use the existing loadUserProgress function but without showing loading screen
      const allActivitiesData = await dailyActivityService.getAllActivities();
      const sortedAll = [...allActivitiesData].sort((a, b) => {
        const ai = Number(a.id);
        const bi = Number(b.id);
        if (Number.isNaN(ai) || Number.isNaN(bi)) {
          return String(a.id).localeCompare(String(b.id));
        }
        return ai - bi;
      });
      
      // Get completed activities from Firestore - retry if needed
      // Also check backend progress as a fallback
      let completedSnap;
      let backendProgress = null;
      
      // First, try to get from backend as it might be more up-to-date
      try {
        backendProgress = await dailyActivityService.getUserProgress(user.uid);
        console.log('✅ Backend progress retrieved:', {
          completedCount: backendProgress.completedActivities?.length || 0,
          completedIds: backendProgress.completedActivities?.map(c => c.activityId) || []
        });
      } catch (backendError) {
        console.warn('⚠️ Could not get backend progress:', backendError);
      }
      
      let retries = 5; // More retries
      while (retries > 0) {
        try {
          const completedQuery = query(
            collection(db, 'userActivities'),
            where('userId', '==', user.uid),
            where('type', '==', 'daily_activity'),
            where('isCompleted', '==', true)
          );
          completedSnap = await getDocs(completedQuery);
          console.log(`✅ Firestore query successful, found ${completedSnap.docs.length} completed activities`);
          
          // Merge backend and Firestore data to get most complete picture
          if (backendProgress && backendProgress.completedActivities) {
            const backendIds = backendProgress.completedActivities.map(c => Number(c.activityId));
            const firestoreIds = completedSnap.docs.map(doc => Number(doc.data().activityId));
            const allCompletedIds = Array.from(new Set([...backendIds, ...firestoreIds]));
            
            if (allCompletedIds.length > firestoreIds.length) {
              console.log('⚠️ Backend has more completed activities, merging data');
              console.log('🔍 Merged completed IDs:', {
                backend: backendIds,
                firestore: firestoreIds,
                merged: allCompletedIds
              });
              // Use the merged list directly
              completedSnap = {
                docs: allCompletedIds.map(id => ({
                  data: () => ({ activityId: id, isCompleted: true })
                }))
              };
            }
          }
          
          break;
        } catch (error) {
          retries--;
          if (retries > 0) {
            console.log(`⚠️ Retrying Firestore query (${retries} retries left)...`);
            await new Promise(resolve => setTimeout(resolve, 500));
          } else {
            console.error('❌ Firestore query failed after all retries:', error);
            // Fallback to backend progress if available
            if (backendProgress && backendProgress.completedActivities) {
              console.log('🔄 Using backend progress as fallback');
              const syntheticDocs = backendProgress.completedActivities.map(c => ({
                data: () => ({ activityId: c.activityId, isCompleted: true })
              }));
              completedSnap = { docs: syntheticDocs };
              break;
            }
            throw error;
          }
        }
      }
      
      // Extract completed activity IDs - handle both string and number
      const completedIds = completedSnap.docs.map(doc => {
        const data = doc.data();
        const activityId = data.activityId;
        return Number(activityId) || activityId;
      });
      const uniqueCompletedIds = Array.from(new Set(completedIds.map(id => Number(id))));
      
      console.log('🔍 Completed activities found:', {
        rawIds: completedSnap.docs.map(doc => doc.data().activityId),
        normalizedIds: uniqueCompletedIds,
        count: uniqueCompletedIds.length
      });

      const progress = {
        completedActivities: uniqueCompletedIds.map(id => ({ activityId: id })),
        totalActivities: sortedAll.length
      };
      
      // Update all states in sequence to force re-render
      setAllActivities(sortedAll);
      setUserProgress(progress);

      // Filter out completed activities - handle both string and number IDs
      const incompleteActivities = sortedAll.filter(activity => {
        const activityIdNum = Number(activity.id);
        const isCompleted = uniqueCompletedIds.includes(activityIdNum);
        if (isCompleted) {
          console.log(`⏭️ Skipping completed activity: ${activity.id} (normalized: ${activityIdNum})`);
        }
        return !isCompleted;
      });
      
      console.log('🎯 Activity status after reload:', {
        totalActivities: sortedAll.length,
        completedIds: uniqueCompletedIds,
        completedCount: uniqueCompletedIds.length,
        incompleteIds: incompleteActivities.map(a => a.id),
        incompleteCount: incompleteActivities.length,
        nextActivity: incompleteActivities[0]?.id,
        nextActivityTitle: incompleteActivities[0]?.title
      });
      
      // Update activities list - this is critical for showing next activity
      if (incompleteActivities.length > 0) {
        const nextActivity = incompleteActivities[0];
        console.log('✅ Setting next activity:', {
          id: nextActivity.id,
          title: nextActivity.title,
          previousActivityId: uniqueCompletedIds[uniqueCompletedIds.length - 1]
        });
        setActivities([nextActivity]);
        setCurrentCardIndex(0);
        console.log('✅ Next activity state updated, should show Activity', nextActivity.id);
      } else {
        setActivities([]);
        setCurrentCardIndex(0);
        console.log('🎉 All activities completed!');
      }
      
      // Force re-render
      setForceUpdate(prev => prev + 1);
      
      // Ensure loading is false
      setLoading(false);
      
      console.log('✅ All state updates complete, UI should refresh now');
      
      // Verify state was actually updated
      setTimeout(() => {
        console.log('🔍 Verifying state update:', {
          activitiesCount: activities.length,
          showCompletionPopup,
          showExpandedView
        });
      }, 500);
      
      // After popup, close it and handle navigation
      setTimeout(() => {
        console.log('🔄 Closing completion popup...');
        setShowCompletionPopup(false);
        
        // If all activities completed, navigate to dashboard
        if (incompleteActivities.length === 0) {
          console.log('🎉 All activities completed, navigating to dashboard');
          setTimeout(() => {
            navigate('/daily-activities');
          }, 500);
        } else {
          console.log('✅ Next activity should now be visible:', incompleteActivities[0]?.id);
          // Force one more re-render to ensure UI updates
          setForceUpdate(prev => prev + 1);
          
          // Fallback: If UI still doesn't update after 1 more second, reload page
          setTimeout(() => {
            console.log('🔄 Fallback: Reloading page to ensure UI updates...');
            window.location.reload();
          }, 2000);
        }
      }, 3000); // 3 seconds to show the message
      
    } catch (error) {
      console.error('❌ Error in handleActivityCompletion:', error);
      console.error('❌ Error stack:', error.stack);
      // Still close popup even if there's an error
      setShowCompletionPopup(false);
      setShowExpandedView(false);
      // Show error to user
      alert('Failed to update UI. Please refresh the page.');
    }
  };

  const refreshUserProgress = async () => {
    try {
      const progress = await dailyActivityService.getUserProgress(user.uid);
      setUserProgress(progress);
      console.log('🔄 Refreshed user progress:', progress);
      
      const allActivities = await dailyActivityService.getAllActivities();
      const sortedAll = [...allActivities].sort((a, b) => Number(a.id) - Number(b.id));

      if (requestedActivityId || requestedSubmissionId) {
        const match = await resolveRequestedActivity(sortedAll);
        if (match) {
          setActivities([match]);
          setCurrentCardIndex(0);
          return;
        }
      }

      const incompleteActivities = sortedAll.filter(activity => 
        !progress.completedActivities?.some(completed => String(completed.activityId) === String(activity.id))
      );
      
      if (incompleteActivities.length > 0) {
        setActivities([incompleteActivities[0]]);
        setCurrentCardIndex(0);
      } else {
        setActivities([]);
        setCurrentCardIndex(0);
      }
      
      console.log('🔄 Updated activities list:', {
        allActivities: allActivities.length,
        totalIncomplete: incompleteActivities.length,
        showingActivity: incompleteActivities[0]?.id || 'none',
        completedActivities: progress.completedActivities?.length || 0
      });
      
    } catch (error) {
      console.error('Error refreshing user progress:', error);
    }
  };

  // Helper functions for activity access control
  const isActivityCompleted = (activityId) => {
    return userProgress?.completedActivities?.some(completed => completed.activityId === activityId) || false;
  };
  
  const isActivityAccessible = (activityIndex) => {
    if (activityIndex === 0) return true; // First activity is always accessible
    // Check if previous activity is completed
    const previousActivity = activities[activityIndex - 1];
    return previousActivity && isActivityCompleted(previousActivity.id);
  };
  
  const isCurrentActivityCompleted = isActivityCompleted(currentActivity?.id);
  const isCurrentActivityAccessible = isActivityAccessible(currentCardIndex);
  const progressPercentage = activities.length > 0 ? (userProgress?.completedActivities?.length / activities.length) * 100 : 0;
  const completedActivities = userProgress?.completedActivities?.length || 0;

  return (
    <>
    <RoomSceneWrapper>
      <KidBackground variant="blobs" intensity={0.22} className="!z-[1]" />
      {/* Navigation - Fixed at top, aligned with window */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center justify-center pointer-events-none">
        <div className="w-full max-w-5xl px-2 sm:px-4 pt-2 sm:pt-4 flex items-center justify-end gap-1 sm:gap-2 pointer-events-auto" style={{
        paddingTop: 'max(0.5rem, calc(env(safe-area-inset-top, 0px) + 0.5rem))',
        paddingLeft: 'max(0.5rem, calc(env(safe-area-inset-left, 0px) + 0.5rem))',
        paddingRight: 'max(0.5rem, calc(env(safe-area-inset-right, 0px) + 0.5rem))'
      }}>
        <div className="flex items-center gap-0.5 md:gap-2">
          <button
            onClick={() => navigate('/my-art-world')}
            className="p-1 md:px-4 md:py-2 bg-white/80 text-amber-800 text-[7px] md:text-sm font-medium rounded-full border border-amber-300/50 shadow-sm hover:bg-white transition-all flex items-center justify-center"
            title="Artist Corner"
          >
            <LayoutDashboard className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0 text-amber-800" />
            <span className="hidden md:inline ml-1">Artist Corner</span>
          </button>
          <button
            onClick={() => navigate('/home')}
            className="p-1 md:px-4 md:py-2 bg-white/80 text-amber-800 text-[7px] md:text-sm font-medium rounded-full border border-amber-300/50 shadow-sm hover:bg-white transition-all flex items-center justify-center"
            title="Home"
          >
            <Home className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0 text-amber-800" />
            <span className="hidden md:inline ml-1">Home</span>
          </button>
          {isAuthenticated && (
            <button
              onClick={handleSignOut}
              className="p-1 md:px-4 md:py-2 bg-white/80 text-amber-800 text-[7px] md:text-sm font-medium rounded-full border border-amber-300/50 shadow-sm hover:bg-white transition-all flex items-center justify-center"
              title="Sign Out"
            >
              <LogOut className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0 text-amber-800" />
              <span className="hidden md:inline ml-1">Sign Out</span>
            </button>
          )}
        </div>
        </div>
      </div>

      {/* Content — inside glass room (RoomSceneWrapper); inner panels stay white for readability */}
      <div className="relative z-10 w-full max-w-5xl mx-auto mt-10 px-2 sm:px-4">
        <div className="p-2 md:p-4">
          
          {/* Motivational Message - Add top padding to avoid button overlap */}
          <div className="text-center mb-6 pt-12 sm:pt-16 md:pt-6">
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mb-3 sm:mb-4 md:mb-6 drop-shadow-lg px-2">
              Your Creative Journey Awaits!
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-black/80 max-w-3xl mx-auto mb-4 sm:mb-6 md:mb-8 drop-shadow-md px-4">
              Start your activity for the day now
            </p>
        </div>

          {/* Activity Card */}
          {activities.length > 0 ? (
            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-amber-200">
              {/* Activity Header */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {currentActivity?.id || currentCardIndex + 1}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-amber-800 font-semibold">
                      Activity {currentActivity?.id || currentCardIndex + 1}
                    </span>
                    {isCurrentActivityCompleted && (
                      <span className="text-green-600 text-sm">✓ Completed</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-amber-600 text-sm font-medium">
                    {userProgress?.completedActivities?.length || 0} completed
                  </div>
                  <div className="text-amber-500 text-xs">
                    {isCurrentActivityCompleted ? 'Great job!' : 'Ready to start'}
                  </div>
                  <div className="text-amber-400 text-xs">
                    {allActivities.length - (userProgress?.completedActivities?.length || 0)} remaining
                  </div>
                  <div className="text-amber-300 text-xs">
                    {allActivities.length} total
                  </div>
                </div>
              </div>

              {/* PDF Thumbnail */}
              <div className="mb-4">
                <PDFThumbnail 
                  activityId={currentActivity?.id}
                  pageNumber={currentActivity?.id}
                  onClick={() => {
                    const activityId = currentActivity?.id;
                    if (!activityId || isNaN(Number(activityId))) {
                      console.error('❌ Cannot open: Invalid activity ID:', activityId);
                      return;
                    }
                    console.log('🎯 Opening activity:', activityId);
                    setShowExpandedView(true);
                  }}
                />
              </div>

              {/* View Full Activity Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const activityId = currentActivity?.id;
                  console.log('🎯 Start activity button clicked');
                  console.log('📋 Current activity:', currentActivity);
                  console.log('📋 Activity ID:', activityId);
                  
                  if (!activityId || isNaN(Number(activityId))) {
                    console.error('❌ Cannot open: Invalid activity ID:', activityId);
                    alert('Error: Invalid activity. Please refresh the page.');
                    return;
                  }
                  
                  console.log('📋 Setting showExpandedView to true for Activity', activityId);
                  setShowExpandedView(true);
                  console.log('✅ showExpandedView set to true');
                }}
                className="w-full bg-amber-400 text-white py-3 px-4 rounded-lg shadow-lg font-medium flex items-center justify-center gap-2 hover:bg-amber-500 transition-colors"
              >
                Start your activity for the day now
              </button>

              {/* No navigation arrows - only one activity shown at a time */}
              {/* Users must complete current activity to unlock next one */}
            </div>
          ) : loading ? (
            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-amber-200 text-center">
              <div className="w-full h-40 rounded-xl bg-amber-50 animate-pulse mb-4" />
              <p className="text-amber-700 font-medium">Getting your activity...</p>
            </div>
          ) : (
            /* All Activities Completed Message */
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-amber-200 text-center">
              <h2 className="text-3xl font-bold text-amber-800 mb-4">Congratulations!</h2>
              <p className="text-amber-700 mb-6 text-lg">
                You have completed all daily activities! Your creative journey is complete for now.
              </p>
              <div className="text-amber-600 text-base mb-6">
                Total completed: {userProgress?.completedActivities?.length || 0} activities
              </div>
              <button
                onClick={() => navigate('/daily-activities')}
                className="bg-amber-400 text-white py-3 px-8 rounded-xl shadow-lg font-medium text-lg"
              >
                View My Completed Activities
              </button>
            </div>
          )}

          {/* Next Reset Info */}
          {userProgress?.nextResetTime && (
            <div className="text-center mt-6">
              <p className="text-amber-700 text-sm">
                {formatTimeUntilNext(userProgress.nextResetTime)}
              </p>
            </div>
          )}

        </div>
      </div>
    </RoomSceneWrapper>

      {/* Success Message for Next Activity — outside room wrapper so fixed overlay isn’t clipped */}
      {showNextActivity && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md mx-4 text-center border border-amber-200">
            <h3 className="text-2xl font-bold text-amber-800 mb-3">Activity Completed!</h3>
            <p className="text-amber-700 mb-4 text-lg">
              {(() => {
                const totalActivities = allActivities.length;
                const completedCount = userProgress?.completedActivities?.length || 0;
                const remainingCount = totalActivities - completedCount;
                
                if (remainingCount > 0) {
                  return `Congratulations! You've completed Activity ${currentActivity?.id || currentCardIndex + 1}!`;
                } else {
                  return "Congratulations! You've completed all activities!";
                }
              })()}
            </p>
            <p className="text-amber-600 mb-4 text-base">
              {(() => {
                const totalActivities = allActivities.length;
                const completedCount = userProgress?.completedActivities?.length || 0;
                const remainingCount = totalActivities - completedCount;
                
                if (remainingCount > 0) {
                  return `Moving to next activity... ${remainingCount} more ${remainingCount === 1 ? 'activity' : 'activities'} remaining.`;
                } else {
                  return "Check your Daily Acts to see all your completed work!";
                }
              })()}
            </p>
            {(() => {
              const totalActivities = allActivities.length;
              const completedCount = userProgress?.completedActivities?.length || 0;
              const remainingCount = totalActivities - completedCount;
              
              if (remainingCount === 0) {
                return (
                  <button
                    onClick={() => navigate('/daily-activities')}
                    className="w-full bg-amber-400 text-white py-3 px-6 rounded-xl font-medium text-lg hover:bg-amber-500 transition-colors"
                  >
                    View My Completed Activities
                  </button>
                );
              }
              return null;
            })()}
          </div>
        </div>
      )}

      {/* Non-blocking refresh veil: keep current content visible while data refreshes */}
      {loading && hasLoadedOnce && (
        <div className="fixed inset-0 z-20 pointer-events-none flex items-start justify-center pt-24">
          <div className="bg-white/85 backdrop-blur-sm border border-amber-200 rounded-full px-4 py-2 shadow text-amber-800 text-sm font-medium">
            Refreshing your activity...
          </div>
        </div>
      )}

      {/* Expanded PDF overlay as window on top of this page */}
      {showExpandedView && (
        <div className="fixed inset-0 z-40">
          <PDFPageDisplayForDailyActs 
            currentActivityId={(() => {
              const activityId = currentActivity?.id;
              console.log('🔍 DEBUG: Expanded view - currentActivity:', currentActivity);
              console.log('🔍 DEBUG: Expanded view - activityId:', activityId);
              console.log('🔍 DEBUG: Expanded view - currentCardIndex:', currentCardIndex);
              return activityId || (currentCardIndex + 1);
            })()}
            isExpanded={true}
            onClose={() => {
              setShowExpandedView(false);
            }}
            onShareFlowComplete={handleShareFlowComplete}
            onNextActivity={handleActivityCompletion}
          />
        </div>
      )}


      {/* No Artwork Popup */}
      {showNoArtworkPopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full mx-4 p-6 text-center">
            <div className="text-4xl mb-4">🔒</div>
            <h3 className="text-xl font-bold text-amber-800 mb-2">Activity Locked</h3>
            <p className="text-amber-600 mb-6">
              You need to complete Activity {currentCardIndex + 1} first before you can access the next activity. Please upload your artwork and mark it as complete.
            </p>
            <button
              onClick={() => setShowNoArtworkPopup(false)}
              className="w-full bg-amber-400 text-white py-3 px-4 rounded-lg font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Completion Success Popup */}
      {showCompletionPopup && (
        <div 
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 10000 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-6 text-center border border-amber-200">
            <h3 className="text-2xl font-bold text-amber-800 mb-3">Awesome!</h3>
            <p className="text-amber-700 mb-6 text-lg">
              {(() => {
                // Determine which activity was JUST completed using progress,
                // not the current card (which may already be showing the next activity).
                const completedList = userProgress?.completedActivities || [];
                const lastCompletedId = completedList.length
                  ? Math.max(
                      ...completedList
                        .map(c => Number(c.activityId))
                        .filter(id => !Number.isNaN(id))
                    )
                  : null;

                const totalActivities = allActivities.length;
                const completedCount = completedList.length || 0;
                const remainingCount = totalActivities - completedCount;

                if (lastCompletedId != null && remainingCount > 0) {
                  return `Congratulations! You've completed Activity ${lastCompletedId}! ${remainingCount} more ${remainingCount === 1 ? 'activity' : 'activities'} ${remainingCount === 1 ? 'is' : 'are'} waiting for you.`;
                }
                if (lastCompletedId != null && remainingCount === 0) {
                  return `Congratulations! You've completed Activity ${lastCompletedId}! Your creative journey is complete for now.`;
                }
                // Fallback message
                return "Congratulations! You've completed your activity!";
              })()}
            </p>
            <button
              onClick={() => {
                setShowCompletionPopup(false);
                // If there are remaining activities, the page will auto-refresh to show next one
                // If all completed, navigate to dashboard
                const totalActivities = allActivities.length;
                const completedCount = userProgress?.completedActivities?.length || 0;
                const remainingCount = totalActivities - completedCount;
                if (remainingCount === 0) {
                  navigate('/daily-activities');
                }
                // Otherwise, next activity should already be loaded
              }}
              className="w-full bg-amber-400 text-white py-3 px-6 rounded-xl font-medium text-lg hover:bg-amber-500 transition-colors"
            >
              {(() => {
                const totalActivities = allActivities.length;
                const completedCount = userProgress?.completedActivities?.length || 0;
                const remainingCount = totalActivities - completedCount;
                return remainingCount > 0 ? 'Continue to Next Activity' : 'View My Completed Activities';
              })()}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DailyArtActivity;

